import clsx, { type ClassValue } from "clsx";
import {
	isValidElement,
	type SyntheticEvent,
	type MouseEvent,
	type ReactNode,
	useRef,
	type CSSProperties,
	useEffect,
	useImperativeHandle,
	type RefCallback,
	useState,
} from "react";
import Controls from "./Controls.tsx";
import Footer, { type FooterRow } from "./Footer.tsx";
import Header from "./Header.tsx";
import Info from "./Info.tsx";
import Row from "./Row.tsx";
import Pagination from "./Pagination.tsx";
import PerPage from "./PerPage.tsx";
import { ResponsiveTableWrapper } from "../ResponsiveTableWrapper.tsx";
import { helpers } from "../../util/helpers.ts";
import type { SortOrder } from "../../../../../common/types.ts";
import type { Col, SuperCol } from "../../../../../common/types/dataTable.ts";
import updateSortBys from "./updateSortBys.ts";
import useStickyXX from "./useStickyXX.ts";
import { useDataTableState } from "./useDataTableState.ts";
import { processRows } from "./processRows.ts";
import { useBulkSelectRows, type SelectedRows } from "./useBulkSelectRows.ts";
import { BulkActions, type BulkAction } from "./BulkActions.tsx";
import { DataTableContext } from "./contexts.ts";
import { useStickyTableHeader } from "./useStickyTableHeader.ts";
import { safeLocalStorage } from "../../util/safeLocalStorage.ts";
import i18n from "../../util/i18n.ts";
import { MobileTooltip } from "../MobileTooltip.tsx";
import type { State } from "./loadStateFromCache.ts";
import MobileListToggle, {
	useMobileListVisibleCount,
} from "../MobileListToggle.tsx";
import {
	MOBILE_VIEWPORT_MEDIA_QUERY,
	isMobileViewport,
} from "../../hooks/useIsMobileViewport.ts";
import { MobileP3TableExportButton } from "../../mobile/MobileP3ShareExportActions.tsx";

const MOBILE_PRUNED_CONTROLS_PER_PAGE = 10;

const shouldPruneControlsForMobile = isMobileViewport;
export type DataTableMobileControls = "hidden" | "expert";

export type SortBy = [number, SortOrder];

// Col / SuperCol are defined in common/ so non-UI code can reference them
// without crossing into src/ui. The titleReact / SuperCol.title fields are
// typed `unknown` there; UI rendering code casts to ReactNode at the
// point of use (see Header.tsx).
export type { Col, SuperCol };

type Season = number | "career";

export type DataTableRowMetadata =
	| {
			type: "player";
			pid: number;
			season:
				| Season
				| {
						// Use this to specify different seasons for different actions
						compare?: Season;
						export?: Season;
						default: Season;
				  };
			playoffs: "playoffs" | "regularSeason" | "combined";
	  }
	| {
			type: "row";
	  };

export type DataTableMobileField = {
	className?: ClassValue;
	label: ReactNode;
	style?: CSSProperties;
	title?: string;
	value: ReactNode;
};

export type DataTableMobileAction = {
	className?: ClassValue;
	label?: ReactNode;
	style?: CSSProperties;
	title?: string;
	value: ReactNode;
};

export type DataTableMobileRow = {
	actions?: DataTableMobileAction[];
	fields?: DataTableMobileField[];
	secondaryFields?: DataTableMobileField[];
	title?: ReactNode;
};

export type DataTableRow = {
	key: number | string;
	data: (
		| ReactNode
		| {
				classNames?: ClassValue;
				style?: CSSProperties;
				value: ReactNode;
				searchValue?: string | number;
				sortValue?: string | number;
				header?: boolean;
				title?: string;
				colSpanToEnd?: boolean; // Maybe dangerous unless disableSort
		  }
	)[];
	classNames?:
		| ClassValue
		| ((args: {
				isDragged: boolean;
				isFiltered: boolean;
				sortBys: SortBy[] | undefined;
		  }) => ClassValue);
	metadata?: DataTableRowMetadata;
	mobile?: DataTableMobileRow;
	rowLabel?: ReactNode;
};

export type StickyCols = 0 | 1 | 2 | 3;

const isDataTableCellObject = (
	value: DataTableRow["data"][number],
): value is Extract<DataTableRow["data"][number], { value: ReactNode }> =>
	value !== null && typeof value === "object" && Object.hasOwn(value, "value");

const getDataTableCellValue = (
	value: DataTableRow["data"][number],
): ReactNode => (isDataTableCellObject(value) ? value.value : value);

const isEmptyMobileCellValue = (value: ReactNode) =>
	value === null || value === undefined || value === "";

const isMobileCheckboxValue = (value: ReactNode) => {
	if (!isValidElement(value) || value.type !== "input") {
		return false;
	}

	const props = value.props as { type?: unknown };
	return props.type === "checkbox";
};

type MobileField = {
	className?: ClassValue;
	label: ReactNode;
	style?: CSSProperties;
	title?: string;
	value: ReactNode;
};

const isIgnorableMobileActionLabel = (label: ReactNode) =>
	typeof label === "string" &&
	(label === "" ||
		label === "#" ||
		label === "X" ||
		label ===
			i18n.t("dataTable.value", {
				ns: "ui",
				defaultValue: "Value",
			}));

const isMobileActionField = (field: MobileField) =>
	isMobileCheckboxValue(field.value);

const getMobileActionLabel = (field: MobileField) => {
	if (!isIgnorableMobileActionLabel(field.label)) {
		return field.label;
	}

	if (isValidElement(field.value)) {
		const props = field.value.props as Record<string, unknown>;
		const ariaLabel = props["aria-label"];
		if (typeof ariaLabel === "string" && ariaLabel) {
			return ariaLabel;
		}

		const title = props.title;
		if (typeof title === "string" && title) {
			return title;
		}
	}

	return i18n.t("dataTable.actions", {
		ns: "ui",
		defaultValue: "Actions",
	});
};

const toMobileField = (field: DataTableMobileField): MobileField => ({
	className: field.className,
	label: field.label,
	style: field.style,
	title: field.title,
	value: field.value,
});

const toMobileActionField = (field: DataTableMobileAction): MobileField => ({
	className: field.className,
	label: field.label ?? "",
	style: field.style,
	title: field.title,
	value: field.value,
});

const getFallbackMobileFields = ({
	colOrder,
	cols,
	row,
}: {
	colOrder: {
		colIndex: number;
		hidden?: boolean;
	}[];
	cols: Col[];
	row: DataTableRow;
}) => {
	let seenColSpanToEnd = false;

	return row.data.flatMap((cell, i) => {
		if (seenColSpanToEnd) {
			return [];
		}
		if (isDataTableCellObject(cell) && cell.colSpanToEnd) {
			seenColSpanToEnd = true;
		}

		const value = getDataTableCellValue(cell);
		if (isEmptyMobileCellValue(value)) {
			return [];
		}

		const col = cols[colOrder[i]?.colIndex ?? i];
		const label =
			(col?.titleReact as ReactNode | undefined) ??
			col?.title ??
			i18n.t("dataTable.value", {
				ns: "ui",
				defaultValue: "Value",
			});

		return [
			{
				className: isDataTableCellObject(cell) ? cell.classNames : undefined,
				label,
				style: isDataTableCellObject(cell) ? cell.style : undefined,
				title: isDataTableCellObject(cell) ? cell.title : undefined,
				value,
			},
		];
	});
};

const getFallbackMobileRowPresentation = (fields: MobileField[]) => {
	let titleIndex = fields.findIndex(
		(field) =>
			!isMobileActionField(field) && !isIgnorableMobileActionLabel(field.label),
	);
	if (titleIndex < 0) {
		titleIndex = fields.findIndex((field) => !isMobileActionField(field));
	}

	const titleField = fields[titleIndex];
	const bodyFields = fields.filter((_, i) => i !== titleIndex);
	const actionFields = bodyFields.filter(isMobileActionField);
	const detailFields = bodyFields.filter(
		(field) => !isMobileActionField(field),
	);

	return {
		actionFields,
		overflowFields: detailFields.slice(6),
		titleField,
		visibleFields: detailFields.slice(0, 6),
	};
};

export const getDataTableMobileRowPresentation = ({
	colOrder,
	cols,
	isFiltered,
	row,
	sortBys,
}: {
	colOrder: {
		colIndex: number;
		hidden?: boolean;
	}[];
	cols: Col[];
	isFiltered: boolean;
	row: DataTableRow;
	sortBys: SortBy[] | undefined;
}) => {
	const fallback = getFallbackMobileRowPresentation(
		getFallbackMobileFields({
			colOrder,
			cols,
			row,
		}),
	);
	const mobile = row.mobile;

	const titleField =
		mobile?.title === undefined
			? fallback.titleField
			: isEmptyMobileCellValue(mobile.title)
				? undefined
				: {
						label: "",
						value: mobile.title,
					};
	const actionFields =
		mobile?.actions === undefined
			? fallback.actionFields
			: mobile.actions
					.map(toMobileActionField)
					.filter((field) => !isEmptyMobileCellValue(field.value));
	const visibleFields =
		mobile?.fields === undefined
			? fallback.visibleFields
			: mobile.fields
					.map(toMobileField)
					.filter((field) => !isEmptyMobileCellValue(field.value));
	const overflowFields =
		mobile?.secondaryFields === undefined
			? fallback.overflowFields
			: mobile.secondaryFields
					.map(toMobileField)
					.filter((field) => !isEmptyMobileCellValue(field.value));

	const rowClassNames =
		typeof row.classNames === "function"
			? row.classNames({
					isDragged: false,
					isFiltered,
					sortBys,
				})
			: row.classNames;

	return {
		actionFields,
		overflowFields,
		row,
		rowClassNames,
		titleField,
		visibleFields,
	};
};

export const getDataTableMobileStateForDisplay = ({
	cols,
	hasMobileRowContract,
	state,
}: {
	cols: Col[];
	hasMobileRowContract: boolean;
	state: State;
}): State => ({
	...state,
	colOrder: cols.map((_, i): { colIndex: number; hidden?: boolean } => ({
		colIndex: i,
	})),
	enableFilters: hasMobileRowContract ? state.enableFilters : false,
	filters: hasMobileRowContract ? state.filters : cols.map(() => ""),
	perPage: MOBILE_PRUNED_CONTROLS_PER_PAGE,
	searchText: "",
	stickyCols: 0 as StickyCols,
});

const MobileCardField = ({ field }: { field: MobileField }) => (
	<div
		className={clsx("datatable-mobile-card-field", "mobile-list-card-field", field.className)}
		data-mobile-list-card-field=""
		style={field.style}
	>
		<dt>
			<MobileTooltip title={field.title}>
				<span>{field.label}</span>
			</MobileTooltip>
		</dt>
		<dd>{field.value}</dd>
	</div>
);

const DataTableMobileList = ({
	colOrder,
	cols,
	isFiltered,
	rows,
	sortBys,
}: {
	colOrder: {
		colIndex: number;
		hidden?: boolean;
	}[];
	cols: Col[];
	isFiltered: boolean;
	rows: DataTableRow[];
	sortBys: SortBy[] | undefined;
}) => {
	const [rowsExpanded, setRowsExpanded] = useState(false);
	const listRef = useRef<HTMLDivElement>(null);

	const fieldsForRows = rows.map((row) =>
		getDataTableMobileRowPresentation({
			colOrder,
			cols,
			isFiltered,
			row,
			sortBys,
		}),
	);
	const rowKeySignature = rows.map((row) => String(row.key)).join("\u0001");

	useEffect(() => {
		setRowsExpanded(false);
	}, [rowKeySignature]);

	const { canCollapse: canCollapseRows, visibleCount } =
		useMobileListVisibleCount({
			expanded: rowsExpanded,
			itemCount: fieldsForRows.length,
			itemSignature: rowKeySignature,
			listRef,
		});
	const fieldsForRowsToRender =
		canCollapseRows && !rowsExpanded
			? fieldsForRows.slice(0, visibleCount)
			: fieldsForRows;

	return (
		<>
			<div
				className="datatable-mobile-list mobile-list-card-list"
				data-mobile-list-card-count={fieldsForRows.length}
				data-mobile-list-card-list=""
				data-mobile-visible-list-card-count={fieldsForRowsToRender.length}
				ref={listRef}
				role="list"
			>
				{fieldsForRowsToRender.map(
					({
						actionFields,
						overflowFields,
						row,
						rowClassNames,
						titleField,
						visibleFields,
					}) => {
						return (
							<article
								className={clsx("datatable-mobile-card", "mobile-list-card", rowClassNames)}
								data-mobile-list-card=""
								data-mobile-list-card-index={row.key}
								data-datatable-mobile-card-overflow-count={overflowFields.length}
								data-datatable-mobile-card-visible-field-count={visibleFields.length}
								key={row.key}
								role="listitem"
							>
								{titleField ? (
									<div
										className="datatable-mobile-card-title mobile-list-card-title"
										data-mobile-list-card-title=""
									>
										{titleField.value}
									</div>
								) : null}
								{actionFields.length > 0 ? (
									<div className="datatable-mobile-card-actions mobile-list-card-actions">
										{actionFields.map((field, i) => (
											<label
												className="datatable-mobile-card-action mobile-list-card-action"
												key={i}
											>
												<span>{getMobileActionLabel(field)}</span>
												{field.value}
											</label>
										))}
									</div>
								) : null}
								<dl
									className="datatable-mobile-card-fields mobile-list-card-fields"
									data-mobile-list-card-fields=""
								>
									{visibleFields.map((field, i) => (
										<MobileCardField field={field} key={i} />
									))}
								</dl>
								{overflowFields.length > 0 ? (
									<details
										className="datatable-mobile-card-more mobile-list-card-more"
										data-datatable-mobile-card-details=""
										data-native-details-host="datatable-mobile-card-detail"
									>
										<summary data-datatable-mobile-card-details-toggle="">
											<span className="datatable-mobile-card-more-closed mobile-list-card-more-closed">
												{i18n.t("dataTable.viewAll", {
													ns: "ui",
													defaultValue: "View all",
												})}
											</span>
											<span className="datatable-mobile-card-more-open mobile-list-card-more-open">
												{i18n.t("dataTable.collapseFields", {
													ns: "ui",
													defaultValue: "Collapse",
												})}
											</span>
										</summary>
										<dl
											className="datatable-mobile-card-fields mobile-list-card-fields"
											data-mobile-list-card-fields=""
										>
											{overflowFields.map((field, i) => (
												<MobileCardField field={field} key={i} />
											))}
										</dl>
									</details>
								) : null}
							</article>
						);
					},
				)}
			</div>
			{canCollapseRows ? (
				<MobileListToggle
					expanded={rowsExpanded}
					onClick={() => {
						setRowsExpanded((prev) => !prev);
					}}
				/>
			) : null}
		</>
	);
};

export type DataTableHandle = {
	setFilters: (filters: string[], enableFilters: boolean) => void;
	getEnableFilters: () => boolean;
	getFilters: () => string[];
};

export type Props = {
	className?: string;
	classNameWrapper?: string;
	clickable?: boolean;
	cols: Col[];
	defaultSort: SortBy | "disableSort";
	disableSettingsCache?: boolean;
	defaultStickyCols?: StickyCols;
	extraBulkActions?: BulkAction[];
	footer?: FooterRow | FooterRow[];
	hideAllControls?: boolean; // When ReactNode, display as a title above the table
	hideHeader?: boolean;
	hideMenuToo?: boolean;
	mobileControls?: DataTableMobileControls;
	mobileP3Export?: {
		label: string;
		season?: number;
	};
	name: string;
	nonfluid?: boolean;
	pagination?: boolean;
	rankCol?: number;
	ref?: RefCallback<DataTableHandle>;
	rows: DataTableRow[];
	showRowLabels?: boolean;
	small?: boolean;
	stickyHeader?: boolean;
	striped?: boolean;
	style?: CSSProperties;
	superCols?: SuperCol[];
	title?: ReactNode;

	// Pass this to control selectedRows from outside of this component (like if you want to have a button external to the table that does something with selected players). Otherwise, leave this undefined.
	controlledSelectedRows?: SelectedRows;
	alwaysShowBulkSelectRows?: boolean; // Often used along with controlledSelectedRows,
	disableBulkSelectKeys?: Set<DataTableRow["key"]>;
};

export const DataTable = ({
	alwaysShowBulkSelectRows,
	className,
	classNameWrapper,
	clickable = true,
	cols,
	controlledSelectedRows,
	defaultSort,
	defaultStickyCols = 0,
	disableBulkSelectKeys,
	disableSettingsCache,
	extraBulkActions,
	footer,
	hideAllControls,
	hideHeader,
	hideMenuToo,
	mobileControls = "hidden",
	mobileP3Export,
	name,
	nonfluid,
	pagination,
	rankCol,
	ref,
	rows,
	showRowLabels,
	small,
	stickyHeader,
	striped,
	style,
	superCols,
	title,
}: Props) => {
	const [mobilePrunedControls, setMobilePrunedControls] = useState(
		shouldPruneControlsForMobile,
	);

	useEffect(() => {
		if (window.mobile) {
			return;
		}

		const mediaQueryList = window.matchMedia(MOBILE_VIEWPORT_MEDIA_QUERY);
		const updateMobilePrunedControls = () => {
			setMobilePrunedControls(shouldPruneControlsForMobile());
		};

		updateMobilePrunedControls();
		mediaQueryList.addEventListener("change", updateMobilePrunedControls);

		return () => {
			mediaQueryList.removeEventListener("change", updateMobilePrunedControls);
		};
	}, []);

	const hideAllControlsBool = !!hideAllControls;
	const mobileDefaultControlsHidden =
		mobilePrunedControls && mobileControls !== "expert";
	const mobileTablePolicy = mobilePrunedControls ? mobileControls : "desktop";
	const effectiveNonfluid = nonfluid && !mobileDefaultControlsHidden;
	const effectiveDefaultStickyCols = mobileDefaultControlsHidden
		? 0
		: defaultStickyCols;
	const { state, setStatePartial, resetState } = useDataTableState({
		cols,
		defaultSort,
		defaultStickyCols: effectiveDefaultStickyCols,
		disableSettingsCache,
		hideAllControls: hideAllControlsBool,
		name,
	});

	const {
		bulkSelectRows,
		metadataType,
		selectedRows,
		showBulkSelectCheckboxes,
		toggleBulkSelectRows,
	} = useBulkSelectRows({
		alwaysShowBulkSelectRows,
		controlledSelectedRows,
		rows,
	});

	const handleColClick = (event: MouseEvent, i: number) => {
		if (state.sortBys !== undefined) {
			const sortBys = updateSortBys({
				cols,
				event,
				i,
				prevSortBys: state.sortBys,
			});

			state.settingsCache.set("DataTableSort", sortBys);
			setStatePartial({
				currentPage: 1,
				sortBys,
			});
		}
	};

	const handleBulkSelectRows = () => {
		toggleBulkSelectRows();
	};

	const handleResetTable = () => {
		state.settingsCache.clear("DataTableColOrder");
		state.settingsCache.clear("DataTableFilters");
		state.settingsCache.clear("DataTableSort");
		state.settingsCache.clear("DataTableStickyCols");

		resetState({
			cols,
			defaultSort,
			defaultStickyCols: effectiveDefaultStickyCols,
			disableSettingsCache,
			hideAllControls: hideAllControlsBool,
			name,
		});

		if (bulkSelectRows) {
			toggleBulkSelectRows();
		}
	};

	const handleToggleFilters = () => {
		// Remove filter cache if hiding, add filter cache if displaying
		if (state.enableFilters) {
			state.settingsCache.clear("DataTableFilters");
		} else {
			state.settingsCache.set("DataTableFilters", state.filters);
		}

		setStatePartial({
			enableFilters: !state.enableFilters,
		});
	};

	const handleFilterUpdate = (
		event: SyntheticEvent<HTMLInputElement>,
		i: number,
	) => {
		const filters = helpers.deepCopy(state.filters);

		filters[i] = event.currentTarget.value;
		setStatePartial({
			currentPage: 1,
			filters,
		});
		state.settingsCache.set("DataTableFilters", filters);
	};

	const handlePagination = (newPage: number) => {
		if (newPage !== state.currentPage) {
			setStatePartial({
				currentPage: newPage,
			});
		}
	};

	const handlePerPage = (perPage: number) => {
		if (perPage !== state.perPage) {
			safeLocalStorage.setItem("perPage", String(perPage));
			setStatePartial({
				currentPage: 1,
				perPage,
			});
		}
	};

	const handleSearch = (event: SyntheticEvent<HTMLInputElement>) => {
		setStatePartial({
			currentPage: 1,
			searchText: event.currentTarget.value,
		});
	};

	// If name changes, it means this is a whole new table and it has a different state (example: Player Stats switching between regular and advanced stats).
	// If colOrder does not match cols, need to run reconciliation code in loadStateFromCache (example: current vs past seasons in League Finances).
	if (
		name !== state.prevName ||
		cols.length > state.colOrder.length ||
		state.hideAllControls !== hideAllControlsBool
	) {
		resetState({
			cols,
			defaultSort,
			defaultStickyCols: effectiveDefaultStickyCols,
			disableSettingsCache,
			hideAllControls: hideAllControlsBool,
			name,
		});
	}

	useImperativeHandle(ref, () => {
		return {
			setFilters(filters: string[], enableFilters: boolean) {
				state.settingsCache.set("DataTableFilters", filters);
				setStatePartial({
					enableFilters,
					filters,
				});
			},
			getEnableFilters() {
				return state.enableFilters;
			},
			getFilters() {
				return state.filters;
			},
		};
	}, [
		setStatePartial,
		state.enableFilters,
		state.filters,
		state.settingsCache,
	]);

	const hasMobileRowContract = rows.some((row) => row.mobile !== undefined);
	const stateForDisplay = mobileDefaultControlsHidden
		? getDataTableMobileStateForDisplay({
				cols,
				hasMobileRowContract,
				state,
			})
		: state;

	const processedRows = processRows({
		cols,
		rankCol,
		rows,
		state: stateForDisplay,
	});
	const numRowsFiltered = processedRows.length;
	const numPages = Math.max(
		1,
		Math.ceil(processedRows.length / stateForDisplay.perPage),
	);
	const currentPage = Math.min(state.currentPage, numPages);
	const start = 1 + (currentPage - 1) * stateForDisplay.perPage;
	let end = start + stateForDisplay.perPage - 1;

	if (end > processedRows.length) {
		end = processedRows.length;
	}

	const processedRowsPage = pagination
		? processedRows.slice(start - 1, end)
		: processedRows;

	const colOrderFiltered = stateForDisplay.colOrder.filter(
		({ hidden, colIndex }) => !hidden && cols[colIndex],
	);

	const highlightCols =
		stateForDisplay.sortBys === undefined
			? []
			: stateForDisplay.sortBys
					.map((sortBy) => sortBy[0])
					.map((i) =>
						colOrderFiltered.findIndex(({ colIndex }) => {
							if (colIndex !== i) {
								return false;
							}

							// Make sure sortSequence is not an empty array - same code is in Header
							const sortSequence = cols[colIndex]!.sortSequence;
							if (sortSequence && sortSequence.length === 0) {
								return false;
							}

							return true;
						}),
					);

	const dataTableContext = {
		clickable,
		disableBulkSelectKeys,
		isFiltered: processedRows.length !== rows.length,
		highlightCols,
		selectedRows,
		showRowLabels,
		showBulkSelectCheckboxes: mobileDefaultControlsHidden
			? false
			: showBulkSelectCheckboxes,
		sortBys: stateForDisplay.sortBys,
	};

	const { stickyClass, tableRef } = useStickyXX(
		stateForDisplay.stickyCols,
		mobileDefaultControlsHidden ? false : showBulkSelectCheckboxes,
	);

	const wrapperRef = useRef<HTMLDivElement>(null);
	const responsiveTableWrapperRef = useRef<HTMLDivElement>(null);

	useStickyTableHeader({
		className,
		containerRef: responsiveTableWrapperRef,
		stickyHeader,
		tableRef,
	});

	const table = (
		<DataTableContext value={dataTableContext}>
			<table
				className={clsx(
					"table table-desktop-hover",
					{
						"table-sm": small !== false,
						"table-striped": striped !== false,
						"table-borderless": striped !== false,
					},
					stickyClass,
				)}
				ref={tableRef}
			>
				{hideHeader ? null : (
					<Header
						bulkSelectProps={{
							disableBulkSelectKeys,
							filteredRows: processedRows,
							filteredRowsPage: processedRowsPage,
							selectedRows,
						}}
						colOrder={colOrderFiltered}
						cols={cols}
						enableFilters={stateForDisplay.enableFilters}
						filters={stateForDisplay.filters}
						handleColClick={handleColClick}
						handleFilterUpdate={handleFilterUpdate}
						showBulkSelectCheckboxes={
							mobileDefaultControlsHidden ? false : showBulkSelectCheckboxes
						}
						showRowLabels={showRowLabels}
						sortable={false}
						sortBys={stateForDisplay.sortBys}
						superCols={superCols}
					/>
				)}
				<tbody>
					{processedRowsPage.map((row) => (
						<Row key={row.key} row={row} />
					))}
				</tbody>
				<Footer colOrder={colOrderFiltered} footer={footer} />
			</table>
		</DataTableContext>
	);

	return (
		<>
			<div className={className} style={style}>
				<div
					className={clsx({
						"d-inline-block mw-100": effectiveNonfluid,
					})}
					data-datatable-current-page={currentPage}
					data-datatable-filtered-row-count={numRowsFiltered}
					data-datatable-name={name}
					data-datatable-page-count={numPages}
					data-datatable-total-row-count={rows.length}
					data-mobile-datatable-policy={mobileTablePolicy}
				>
					{!hideAllControls || !hideMenuToo || title ? (
						<div
							className="d-flex align-items-end"
							style={{
								// minHeight rather than height allows title to expanad this, like on player profile pages for the tabs above stat tables
								minHeight: 35,
							}}
							ref={wrapperRef}
						>
							{bulkSelectRows && !mobileDefaultControlsHidden ? (
								<BulkActions
									extraActions={extraBulkActions}
									hasTitle={title !== undefined}
									hideAllControls={hideAllControls}
									name={name}
									selectedRows={selectedRows}
									wrapperRef={wrapperRef}
								/>
							) : pagination && !hideAllControls && !mobileDefaultControlsHidden ? (
								<PerPage onChange={handlePerPage} value={state.perPage} />
							) : null}
							{title ? (
								<div
									className={clsx(
										"datatable-header-text text-truncate d-flex align-items-center",
										bulkSelectRows ? "ms-2" : undefined,
									)}
								>
									{title}
								</div>
							) : null}
							{!hideMenuToo ? (
								<Controls
									alwaysShowBulkSelectRows={!!alwaysShowBulkSelectRows}
									bulkSelectRows={bulkSelectRows}
									enableFilters={state.enableFilters}
									hideAllControls={hideAllControls}
									metadataType={metadataType}
									mobileControlsHidden={mobileDefaultControlsHidden}
									mobilePrunedControls={mobileDefaultControlsHidden}
									name={name}
									onBulkSelectRows={handleBulkSelectRows}
									onResetTable={handleResetTable}
									onSearch={handleSearch}
									onToggleFilters={handleToggleFilters}
									searchText={state.searchText}
								/>
							) : null}
						</div>
					) : null}
					<ResponsiveTableWrapper
						className={clsx(
							classNameWrapper,
							pagination ? "fix-margin-pagination" : null,
						)}
						nonfluid={effectiveNonfluid}
						ref={responsiveTableWrapperRef}
					>
						{mobileDefaultControlsHidden ? (
							<DataTableMobileList
								colOrder={colOrderFiltered}
								cols={cols}
								isFiltered={processedRows.length !== rows.length}
								rows={processedRowsPage}
								sortBys={stateForDisplay.sortBys}
							/>
						) : (
							table
						)}
					</ResponsiveTableWrapper>
					{mobileP3Export ? (
						<div className="mt-2">
							<MobileP3TableExportButton
								colOrder={colOrderFiltered}
								cols={cols}
								filenameLabel={mobileP3Export.label}
								rows={processedRows}
								season={mobileP3Export.season}
							/>
						</div>
					) : null}
					{!hideAllControls && pagination && !mobileDefaultControlsHidden ? (
						<div className="d-flex align-items-center">
							<Info
								end={end}
								numRows={numRowsFiltered}
								numRowsUnfiltered={rows.length}
								start={start}
							/>
							<Pagination
								currentPage={currentPage}
								numRows={numRowsFiltered}
								onClick={handlePagination}
								perPage={stateForDisplay.perPage}
							/>
						</div>
					) : null}
				</div>
			</div>
		</>
	);
};
